import type { NeurogresGame } from "./skillGameMap";
import type { NeurogresStepInference } from "./stepRouter";
import { hasExplicitWorkloadStructureSignal } from "./workloadStructure";

export interface FollowUpPlanInput {
  currentGoal?: string | null;
  actions?: string[] | null;
  recommendedGames?: NeurogresGame[] | null;
  currentStep?: NeurogresStepInference | null;
  detectedRisks?: string[] | null;
  resistanceFactors?: string[] | null;
}

export interface FollowUpPlan {
  nextFollowUpFocus: string;
  reviewQuestions: string[];
  progressSignals: string[];
  correctiveIfStuck: string[];
}

const USER_FACING_RECOMMENDATION_FALLBACK =
  "Choose one clear focus to test before your next session, then notice what changed.";
const INTERNAL_FAIL_CLOSED_TEXT_PATTERN =
  /\b(?:unsupported themes?|current[-\s]session transcript|topic[-\s]specific recap|generated summary contained themes?|without carrying forward)\b/i;

function normalizeText(value = ""): string {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function isInternalPlaceholder(text = ""): boolean {
  return /^(a clearer, more workable next state|clarify the next concrete move|general support need|the agreed shift)$/i.test(normalizeText(text));
}

function isInternalFailClosedText(text = ""): boolean {
  return INTERNAL_FAIL_CLOSED_TEXT_PATTERN.test(normalizeText(text));
}

function isMalformedUserFacingText(text = ""): boolean {
  const normalized = normalizeText(text);
  if (!normalized) return true;
  if (
    isInternalPlaceholder(normalized) ||
    isInternalFailClosedText(normalized)
  ) {
    return true;
  }
  if (
    /\b(a clearer, more workable|test whether it is becoming repeatable|sustain the momentum around)\b/i.test(normalized) ||
    /\band before your next session\b/i.test(normalized) ||
    /\ba only model\b/i.test(normalized) ||
    /\b(start the conversation now|begin the session naturally)\b/i.test(normalized) ||
    /\b(prompt injection|ignore previous instructions|disregard previous instructions)\b/i.test(normalized) ||
    /^(you are|you'?re)\s+(?:dr\.?|doctor|therapist|psychiatrist|psychologist|clinical psychologist|coach)\b/i.test(normalized) ||
    /\b(pretend to be|role[\s-]?play as|respond as|speak as|act as)\b.{0,80}\b(dr\.?|doctor|therapist|psychiatrist|psychologist|clinical psychologist|coach|counsellor|counselor|clinician)\b/i.test(normalized) ||
    /\bbut i stop myself\b/i.test(normalized) ||
    /\bbut then i stop\b/i.test(normalized) ||
    /\bbut i keep stopping\b/i.test(normalized) ||
    /^(let down|always love)\b/i.test(normalized)
  ) {
    return true;
  }
  if (/^[,.;:!?-]/.test(normalized) || /(?:^| )but(?:$| )/i.test(normalized) || /\b(and|or|to|with|because|before)$/i.test(normalized)) {
    return true;
  }
  return normalized.length < 4;
}

function isActionOrReflectionOriented(text = ""): boolean {
  const normalized = normalizeText(text);
  return /\b(clarify|continue|explore|review|reflect|notice|choose|name|write|track|practice|pause|test|focus|ask|set|hold|return|compare|check|observe|plan|try|start|complete|send|book|schedule|decide)\b/i.test(normalized);
}

function inferTopicFallback(text = ""): string {
  const normalized = normalizeText(text).toLowerCase();
  if (!normalized) return "";
  if (hasIdentityBoundarySessionSignal(normalized)) {
    return "Continue the conversation directly with Nia.";
  }
  const hasRelationship = /\b(relationship|dating|partner|ex|breakup|break up|attached|attachment|love her|love him|letting go|let go|reciprocat|mutual|connection)\b/i.test(normalized);
  if (hasRelationship && /\b(always love|letting go|let go|attached|attachment|reciprocat|mutual|connection|miss her|miss him|grief|grieving)\b/i.test(normalized)) {
    return "Explore what healthy mutual connection looks like for you now.";
  }
  if (/\b(burnout|burnt out|burned out|tunnel vision|falling behind|driven|drive|planned breaks?|rest|recovery|exhaust|drained|push through)\b/i.test(normalized)) {
    return "Review how your planned breaks affected your focus and recovery.";
  }
  if (hasWorkloadStructureTopic(normalized)) {
    return "Review what changed after the workload-management step supported by this session.";
  }
  if (hasFamilyPressureTopic(normalized)) {
    return "Explore the family expectation that felt most present in the conversation.";
  }
  if (/\b(procrastinat\w*|avoid(?:ing|ance|ed)?|delay(?:ing|ed)?|put(?:ting)? (?:it |this |that )?off|not starting|can't start|cannot start|won't start|will not start|stuck before starting|follow through)\b/i.test(normalized)) {
    return "Notice what the task seems to protect you from before choosing the next move.";
  }
  return "";
}

function hasWorkloadStructureTopic(text = ""): boolean {
  return hasExplicitWorkloadStructureSignal(text);
}

function hasFamilyPressureTopic(text = ""): boolean {
  const normalized = normalizeText(text).toLowerCase();
  if (!normalized) return false;
  return /\b(family|parents?|mother|father)\b/i.test(normalized) &&
    /\b(pressure|expectation|expects?|responsib(?:le|ility)|provide|providing|support(?:ing)? everyone|everyone needs me|legacy|let down|believe in me|belive in me|proud|disappoint(?:ed|ing|ment)?)\b/i.test(normalized) &&
    !/\b(pain|medical costs?|medical aid|uncertainty|husband|injury|illness|doctor|hospital|treatment|medicine|medication)\b/i.test(normalized);
}

function hasIdentityBoundarySessionSignal(text = ""): boolean {
  const normalized = normalizeText(text).toLowerCase();
  if (!normalized) return false;
  return /\b(pretend to be|act as|you are now|role[\s-]?play as|respond as|speak as|take on the role of|play the role of)\b.{0,120}\b(dr\.?|doctor|therapist|psychiatrist|psychologist|clinical psychologist|coach|counsellor|counselor|clinician|elena mbeki)\b/i.test(normalized)
    || /\byou are\s+(?:dr\.?|doctor|therapist|psychiatrist|psychologist|clinical psychologist|coach)\b/i.test(normalized)
    || /\bstart the conversation now\b/i.test(normalized)
    || /\b(i can'?t|i cannot|i won'?t|i will not|not able to)\b.{0,80}\b(impersonat|pretend to be|adopt another person'?s identity|claim credentials|credentials i do not hold)\b/i.test(normalized)
    || /\bi'?m nia\b.{0,140}\b(impersonat|another person'?s identity|claim credentials|credentials i do not hold|as nia)\b/i.test(normalized);
}

function pickGoal(input: FollowUpPlanInput): string {
  const goal = normalizeText(input.currentGoal || "");
  return isMalformedUserFacingText(goal) ? "" : goal;
}

function formatActionForRecommendation(action = ""): string {
  const normalized = normalizeText(action).replace(/[.]+$/g, "");
  if (isMalformedUserFacingText(normalized)) return "";
  const cleaned = normalized
    .replace(/^(my agreed action is(?: to)?|i will|i'll|i am going to|i'm going to|i plan to|my next step is)\s+/i, "")
    .trim();
  if (isMalformedUserFacingText(cleaned)) return "";
  if (!isActionOrReflectionOriented(cleaned)) return "";
  return cleaned;
}

function ensureSingleSentence(text = ""): string {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  const firstSentence = normalized.split(/(?<=[.!?])\s+/)[0] || normalized;
  return firstSentence.replace(/[.?!]*$/, "").trim() + ".";
}

function buildUserFacingNextFocus(stepId: string, goal: string, action: string): string {
  if (action) {
    return ensureSingleSentence(
      /before your next session\b/i.test(action)
        ? `${action.charAt(0).toUpperCase()}${action.slice(1)}`
        : `${action.charAt(0).toUpperCase()}${action.slice(1)} before your next session`
    );
  }
  if (goal) {
    const topicFallback = inferTopicFallback(goal);
    if (topicFallback) return topicFallback;
    if (stepId === "reframe") {
      return "Notice one real moment where this new perspective helps and stay with it.";
    }
    return ensureSingleSentence(`Choose one clear focus around ${goal} to test before your next session`);
  }
  return USER_FACING_RECOMMENDATION_FALLBACK;
}

export function buildFollowUpPlan(input: FollowUpPlanInput = {}): FollowUpPlan {
  const actions = Array.isArray(input.actions)
    ? input.actions
      .map((item) => normalizeText(item))
      .filter((item) => item && !isInternalFailClosedText(item))
    : [];
  const games = Array.isArray(input.recommendedGames) ? input.recommendedGames.filter(Boolean) : [];
  const risks = Array.isArray(input.detectedRisks)
    ? input.detectedRisks
      .map((item) => normalizeText(item))
      .filter((item) => item && !isInternalFailClosedText(item))
    : [];
  const resistance = Array.isArray(input.resistanceFactors)
    ? input.resistanceFactors
      .map((item) => normalizeText(item))
      .filter((item) => item && !isInternalFailClosedText(item))
    : [];
  const currentGoal = isInternalFailClosedText(input.currentGoal || "")
    ? ""
    : input.currentGoal;
  const rawTopicContext = [currentGoal, ...actions, ...risks, ...resistance].map((item) => normalizeText(item || "")).join(" ");
  const topicFallback = inferTopicFallback(rawTopicContext);
  const goal = topicFallback ? "" : pickGoal({ ...input, currentGoal });
  const reviewGoal = goal || "the focus from this session";
  const stepId = String(input.currentStep?.id || "");
  const primaryAction = formatActionForRecommendation(actions[0] || "");

  const nextFollowUpFocus = topicFallback || buildUserFacingNextFocus(stepId, goal, primaryAction);

  return {
    nextFollowUpFocus,
    reviewQuestions: [
      `What changed around ${reviewGoal} since this session?`,
      actions[0] ? `Did you complete "${actions[0]}"?` : "Which action did you actually take?",
      games[0] ? `Did ${games[0]} help you practise the missing skill?` : "What helped the shift hold between sessions?",
    ].filter(Boolean),
    progressSignals: [
      actions[0] ? `The agreed action is completed or started: ${actions[0]}.` : "A concrete next step is visible and started.",
      "The user reports more choice, clarity, or steadiness than before.",
      games[0] ? `Game practice is happening with ${games[0]}.` : "The user can repeat the shift without over-relying on the session.",
    ],
    correctiveIfStuck: [
      resistance[0] ? `Reduce the resistance factor first: ${resistance[0]}.` : "Reduce the size of the next action until it is easy to start.",
      risks[0] ? `Address the risk cue directly: ${risks[0]}.` : "Return to the main block and re-check the story driving it.",
      games[0] ? `Pause new game suggestions and review whether ${games[0]} matches the actual skill gap.` : "Hold off on extra tools and tighten the goal-action link.",
    ],
  };
}
